import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { IProject } from '@packages/global';
import LazyImage from '../common/LazyImage';
import { useTranslation } from 'react-i18next';

interface ProjectCardProps {
  project: IProject;
  onEdit: (project: IProject) => void;
  onDelete: (id: string) => void;
}

export default function ProjectCard({ project, onEdit, onDelete }: ProjectCardProps) {
  const { t, i18n } = useTranslation(['project', 'common']);
  const navigate = useNavigate();

  const handleViewDetail = () => {
    if (!project.episodes || project.episodes.length === 0) {
      navigate(`/project/${project.id}`);
    } else {
      navigate(`/project/${project.id}?tab=episodes`);
    }
  };

  return (
    <div
      className="bg-bg-surface w-64 rounded-2xl border border-border-subtle shadow-sm hover:shadow-[0_0_20px_rgba(255,92,0,0.15)] transition-all duration-300 cursor-pointer hover:border-primary hover:-translate-y-1 aspect-[3/4] overflow-hidden group"
      onClick={handleViewDetail}
    >
      {project.thumbnailUrl ? (
        <div className="relative h-full">
          <LazyImage
            src={project.thumbnailUrl}
            alt={project.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            placeholderClassName="w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-lg font-semibold text-white mb-1 truncate">{project.name}</h3>
            <p className="text-gray-300 text-sm line-clamp-2 mb-3">{project.description}</p>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => onEdit(project)}
                className="p-2.5 rounded-lg bg-black/20 hover:bg-black/40 text-white/80 hover:text-white transition-all backdrop-blur-sm cursor-pointer"
                title={t('project:editProject')}
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => onDelete(project.id)}
                className="p-2.5 rounded-lg bg-black/20 hover:bg-red-500/80 text-white/80 hover:text-white transition-all backdrop-blur-sm cursor-pointer"
                title={t('project:deleteProject')}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-6 flex flex-col h-full">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-50">{project.name}</h3>
            </div>
          </div>
          <p className="text-gray-400 mb-4 line-clamp-3 flex-1">{project.description}</p>
          <div className="mt-auto">
            <div className="text-sm text-gray-500 mb-4">
              {t('project:createdAt')}: {new Date(project.createdAt).toLocaleDateString(i18n.language)}
            </div>

            <div className="flex gap-2 pt-4 border-t border-border-subtle" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => onEdit(project)}
                className="p-2.5 rounded-lg bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-gray-50 transition-all cursor-pointer"
                title={t('project:editProject')}
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => onDelete(project.id)}
                className="p-2.5 rounded-lg bg-bg-elevated hover:bg-red-500 text-gray-300 hover:text-gray-50 transition-all cursor-pointer"
                title={t('project:deleteProject')}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
